import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { LANGUAGE_OPTIONS, SbSelectBase, SbSelectItem, sbSelectProviders } from './sb-select-base.component';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InfoPopoverComponent } from '../../info-popover/info-popover.component';

interface WidgetLanguagesResponse {
  success: boolean;
  data: { value: string; label: string; dir: string }[];
}

/**
 * Shared across every instance so a page with several language pickers issues
 * one request, not one per control.
 */
let languages$: Observable<SbSelectItem[]> | null = null;

@Component({
  selector: 'app-sb-input-language',
  templateUrl: './sb-select.component.html',
  styleUrls: ['./sb-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: sbSelectProviders(() => SbInputLanguageComponent),
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectComponent, NgLabelTemplateDirective, NgOptionTemplateDirective, ReactiveFormsModule, FormsModule, InfoPopoverComponent]
})
export class SbInputLanguageComponent extends SbSelectBase implements OnInit {
  constructor() {
    super();
    this.mode.set('language');
  }

  ngOnInit(): void {
    this.mode.set('language');
    this.resolveItems();
  }

  /**
   * The supported locales are the server's registry, not a list this component
   * gets to invent — `LANGUAGE_OPTIONS` is only the offline fallback so the
   * design-system showcase still renders without an API.
   */
  protected override resolveItems(): void {
    if (this.mode() !== 'language') {
      super.resolveItems();
      return;
    }
    this.resolvedItems.set([...LANGUAGE_OPTIONS]);
    this.loading.set(true);
    this.fetchLanguages().subscribe((items) => {
      this.loading.set(false);
      if (items.length) {
        this.resolvedItems.set(items);
      }
    });
  }

  private fetchLanguages(): Observable<SbSelectItem[]> {
    if (!languages$) {
      languages$ = this.http
        .get<WidgetLanguagesResponse>(`${environment.apiUrl}/api/v1/share/widget_languages`)
        .pipe(
          map((response) => (response?.data || []).map((locale) => ({
            id: locale.value,
            name: locale.label,
          }))),
          catchError(() => of([] as SbSelectItem[])),
          shareReplay(1)
        );
    }
    return languages$;
  }
}
